import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { isString } from "vue-composable";
import { load } from "../../api/medicus";
import { MenuItem } from "../../layouts/MedicusLayout/SidebarMenu";
import { useAuth } from "../auth";

const APP_TITLE = "Medicus";

function useTitle(overrideTitle: string | null = null) {
  const title = ref<string | null>(isString(overrideTitle) ? overrideTitle : document.title);
  const observer = new MutationObserver((m) => {
    title.value = m[0].target.textContent;
  });
  watch(
    title,
    (t, o) => {
      if (isString(t) && t !== o) {
        document.title = t;
      }
    },
    {
      immediate: true,
    },
  );
  const titleElement = document.querySelector("title")!;
  observer.observe(titleElement, { childList: true });

  return title;
}

export const useApp = defineStore("app", () => {
  // DATASTORE PROPERTY: Title
  const appTitle = useTitle(APP_TITLE);
  const title = ref("");
  const patientTitle = ref("");
  const headerHeight = ref(0);
  const sidebarExpanded = ref(true);

  watch(title, (t) => {
    appTitle.value = [t, APP_TITLE].filter(Boolean).join(" | ");
  });

  function updateTitle(t: string) {
    title.value = t;
  }

  const screenReaderTitle = computed(() =>
    typeof title?.value === "string"
      ? title.value
          .replace(/\s*\|\s*/g, ", ")
          .replace(/\s{2,}/g, " ")
          .trim()
      : "",
  );

  // ENG-15163, WCAG
  function updatePatientTitle(p: string) {
    const suffix = `, ${p}`;

    if (!title.value.includes(suffix) && !title.value.includes(p)) {
      title.value = `${title.value.trim()}${suffix}`;
    }
    if (!patientTitle.value.includes(suffix)) {
      patientTitle.value = `${p}`;
    }
  }

  function updateSidebarStatus(s: boolean) {
    sidebarExpanded.value = s;
  }

  function setHeaderHeight(targetClass = "page-header", offest = 61) {
    const header: HTMLElement = document.getElementsByClassName(targetClass)
      ? document.getElementsByClassName(targetClass)[0]
      : null;
    headerHeight.value = header ? header.offsetHeight + offest : 0;
  }

  // DATASTORE PROPERTY: Modules Menu Items
  const modulesMenuItems = ref<MenuItem[]>([]);
  const auth = ref(useAuth());

  async function fetchModulesMenuItems() {
    if (modulesMenuItems.value.length === 0) {
      const response = await load<{ items: MenuItem[][] }>("navigation/ui/main-menu", {
        showError: false,
      }).then((x) => x.data.items[0]);

      //INFO: Assume route has permission to prevent having to call permission-check API — pre-condition on response returning permitted items only
      modulesMenuItems.value = response.map((item) => ({ ...item, permission: true }));
    }
  }

  function clearModulesMenuItems() {
    modulesMenuItems.value = [];
  }

  // DATASTORE PROPERTY: Keyboard shortcuts modal visibility
  const isKeyboardShortcutsModalVisible = ref(false);
  function setKeyboardShortcutsModalVisible(isOpen: boolean) {
    isKeyboardShortcutsModalVisible.value = isOpen;
  }

  watch(
    () => auth.value.currentTenant,
    (tenant) => {
      if (!tenant) return;
      clearModulesMenuItems();
    },
    { immediate: true, flush: "sync" },
  );

  // DATASTORE PROPERTY: Is Docked Modal Open
  const isDockedModalOpen = ref<boolean>(false);
  const dockedModalTitle = ref<string>();

  const isModalOpen = ref<boolean>(false);
  const openModalTitle = ref<string>();

  function setIsDockedModalOpen(isOpen: boolean) {
    isDockedModalOpen.value = isOpen;
  }

  function setDockedModalTitle(title?: string) {
    dockedModalTitle.value = title;
  }

  return {
    clearModulesMenuItems,
    dockedModalTitle,
    fetchModulesMenuItems,
    headerHeight,
    isDockedModalOpen,
    isKeyboardShortcutsModalVisible,
    isModalOpen,
    modulesMenuItems,
    openModalTitle,
    setDockedModalTitle,
    setHeaderHeight,
    setIsDockedModalOpen,
    setKeyboardShortcutsModalVisible,
    sidebarExpanded,
    title,
    patientTitle,
    updatePatientTitle,
    screenReaderTitle,
    updateTitle,
    updateSidebarStatus,
  };
});
